import { useState } from "react";
import { ArrowLeft, ArrowRight, ShoppingBag } from "@phosphor-icons/react";
import { formatPrice, swatchStyle } from "@/pages/shop/shopHelpers";

/**
 * Product tile on the /shop grid. Handles per-color image cycling (arrow
 * buttons + swatch dots) and delegates the click-to-open flow to the parent
 * via `onOpen(product)`. Extracted from Shop.jsx.
 */
export default function ProductCard({ p, onOpen }) {
  const colors = Array.isArray(p.colors) ? p.colors : [];
  const sizes = Array.isArray(p.sizes) ? p.sizes : [];
  // Color-specific previews from backend (per-color first-variant image). Falls back
  // to the product thumbnail so single-color items still render cleanly.
  const previews = Array.isArray(p.color_previews) && p.color_previews.length
    ? p.color_previews
    : (p.thumbnail_url ? [{ color: colors[0] || "", image: p.thumbnail_url }] : []);
  const hasMulti = previews.length > 1;
  const [idx, setIdx] = useState(0);
  const active = previews[idx] || previews[0];
  const sizeText = sizes.length === 0 ? null : sizes.join(" · ");
  const step = (dir) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIdx((i) => (i + dir + previews.length) % previews.length);
  };
  return (
    <div
      data-testid={`shop-card-${p.id}`}
      className="text-left group border border-white/15 bg-[#0F0F0F] hover:border-[#B32027] transition-colors flex flex-col"
    >
      <div
        role="button"
        tabIndex={0}
        data-testid={`shop-card-image-${p.id}`}
        onClick={() => onOpen(p)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } }}
        style={{ touchAction: "manipulation" }}
        className="relative aspect-square bg-white overflow-hidden block cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#B32027] select-none"
      >
        {active?.image ? (
          <img
            src={active.image}
            alt={p.name + (active.color ? ` — ${active.color}` : "")}
            draggable={false}
            className="w-full h-full object-contain md:group-hover:scale-105 transition-transform duration-500 pointer-events-none"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <ShoppingBag size={48} weight="thin" />
          </div>
        )}
        {hasMulti && (
          <>
            <button
              type="button"
              onClick={step(-1)}
              aria-label="Previous color"
              data-testid={`shop-card-color-prev-${p.id}`}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-[#B32027] text-white flex items-center justify-center backdrop-blur-sm"
            >
              <ArrowLeft size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={step(1)}
              aria-label="Next color"
              data-testid={`shop-card-color-next-${p.id}`}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-[#B32027] text-white flex items-center justify-center backdrop-blur-sm"
            >
              <ArrowRight size={14} weight="bold" />
            </button>
            {active?.color && (
              <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white bg-black/70 px-2 py-1">
                {active.color}
              </span>
            )}
          </>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col justify-between gap-3">
        <h3 onClick={() => onOpen(p)} className="cursor-pointer font-display font-black text-xl uppercase tracking-tight leading-tight group-hover:text-[#B32027] transition-colors">
          {p.name}
        </h3>
        {hasMulti && (
          <div data-testid={`shop-card-swatches-${p.id}`} className="flex flex-wrap gap-1.5">
            {previews.map((cp, i) => (
              <button
                key={cp.color || i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                aria-label={`Show ${cp.color}`}
                title={cp.color}
                className={`w-5 h-5 rounded-full border-2 ${i === idx ? "border-[#B32027]" : "border-white/30 hover:border-white"}`}
                style={swatchStyle(cp.color)}
              />
            ))}
          </div>
        )}
        <div className="space-y-2">
          <div className="flex items-end justify-between gap-3">
            <span data-testid={`shop-card-price-${p.id}`} className="text-lg font-bold text-white">
              {formatPrice(p.price_from, p.price_to, p.currency)}
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50">
              View {p.variant_count > 1 ? `· ${p.variant_count}` : ""}
            </span>
          </div>
          {sizeText && (
            <div className="pt-2 border-t border-white/10">
              <p data-testid={`shop-card-sizes-${p.id}`} className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                <span className="text-white/40 font-bold mr-1">Sizes ·</span> {sizeText}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
